import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, Flag, X } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api, media, timeAgo } from "@/lib/api";
import { ReportDialog } from "./ReportForm";

const DURATION = 6000;

export function StoryViewer({ groups, startIndex, open, onOpenChange }) {
  const [gi, setGi] = useState(startIndex || 0);
  const [ii, setIi] = useState(0);
  const [progress, setProgress] = useState(0);
  const [reportOpen, setReportOpen] = useState(false);
  const raf = useRef(null);
  const started = useRef(0);
  const g = groups[gi];
  const item = g?.items[ii];

  useEffect(() => {
    if (open) {
      setGi(startIndex || 0);
      setIi(0);
    }
  }, [open, startIndex]);

  const next = useCallback(() => {
    if (!g) return;
    if (ii < g.items.length - 1) setIi(ii + 1);
    else if (gi < groups.length - 1) {
      setGi(gi + 1);
      setIi(0);
    } else onOpenChange(false);
  }, [g, gi, ii, groups.length, onOpenChange]);

  const prev = useCallback(() => {
    if (ii > 0) setIi(ii - 1);
    else if (gi > 0) {
      setGi(gi - 1);
      setIi(0);
    }
  }, [gi, ii]);

  useEffect(() => {
    if (!open || !item) return;
    api.post(`/public/stories/${item.id}/view`).catch(() => {});
  }, [open, item]);

  useEffect(() => {
    if (!open || !item || reportOpen) return undefined;
    const reduce = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    started.current = performance.now() - progress * DURATION;
    const tick = (t) => {
      const p = Math.min(1, (t - started.current) / DURATION);
      setProgress(p);
      if (p >= 1) {
        setProgress(0);
        next();
        return;
      }
      raf.current = requestAnimationFrame(tick);
    };
    if (!reduce) raf.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, gi, ii, reportOpen]);

  useEffect(() => setProgress(0), [gi, ii]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (reportOpen) return;
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, next, prev, reportOpen]);

  if (!g || !item) return null;
  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          className="h-[100dvh] max-h-[100dvh] w-full max-w-full gap-0 overflow-hidden border-0 bg-black p-0 sm:h-[92vh] sm:max-w-[440px] sm:rounded-[16px] sm:border sm:border-lux-border [&>button]:hidden"
          data-testid="stories-viewer"
        >
          <DialogTitle className="sr-only">Story from {g.display_name}</DialogTitle>
          <DialogDescription className="sr-only">Story {ii + 1} of {g.items.length}. Use arrow keys to move between stories.</DialogDescription>
          <div className="relative h-full w-full">
            <img src={media(item.url)} alt={`Story photo from ${g.display_name}`} className="absolute inset-0 h-full w-full object-cover" />
            <div className="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-black/75 to-transparent" />
            <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-black/85 to-transparent" />
            <div className="absolute inset-x-3 top-3 flex gap-1" aria-hidden="true">
              {g.items.map((it, i) => (
                <div key={it.id} className="h-[2px] flex-1 overflow-hidden rounded-full bg-white/25">
                  <div className="h-full bg-lux-silverBright" style={{ width: i < ii ? "100%" : i === ii ? `${progress * 100}%` : "0%" }} />
                </div>
              ))}
            </div>
            <div className="absolute inset-x-3 top-6 flex items-center gap-3">
              <div className="story-ring h-9 w-9 shrink-0">
                <img src={media(g.cover)} alt="" className="h-full w-full rounded-full object-cover" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-lux-text">{g.display_name} {g.is_demo && <span className="lux-tag-demo ml-1">Demo</span>}</p>
                <p className="text-xs text-lux-muted">{g.area} · {timeAgo(item.posted_at)}</p>
              </div>
              <button onClick={() => onOpenChange(false)} className="rounded-full p-2 text-lux-silver hover:bg-white/10 hover:text-lux-silverBright" aria-label="Close stories" data-testid="stories-viewer-close-button">
                <X className="h-5 w-5" />
              </button>
            </div>
            <button className="absolute bottom-24 left-0 top-20 w-1/3" onClick={prev} aria-label="Previous story" data-testid="stories-viewer-prev" />
            <button className="absolute bottom-24 right-0 top-20 w-2/3" onClick={next} aria-label="Next story" data-testid="stories-viewer-next" />
            <div className="absolute inset-x-4 bottom-5 flex items-center gap-2">
              <Link to={`/profile/${g.profile_slug}`} onClick={() => onOpenChange(false)} className="lux-btn lux-btn-primary lux-btn-sm flex-1" data-testid="stories-viewer-profile-link">
                View profile
              </Link>
              <button onClick={() => setReportOpen(true)} className="lux-btn lux-btn-secondary lux-btn-sm bg-black/40" data-testid="stories-viewer-report-button">
                <Flag className="h-4 w-4" /> Report
              </button>
              <span className="hidden sm:inline-flex">
                <button onClick={prev} className="rounded-full p-2 text-lux-silver hover:bg-white/10" aria-label="Previous"><ChevronLeft className="h-5 w-5" /></button>
                <button onClick={next} className="rounded-full p-2 text-lux-silver hover:bg-white/10" aria-label="Next"><ChevronRight className="h-5 w-5" /></button>
              </span>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <ReportDialog open={reportOpen} onOpenChange={setReportOpen} storyId={item.id} title="Report this story" />
    </>
  );
}

export default function StoriesRail({ groups = [], title = "Today's stories", subtitle }) {
  const [open, setOpen] = useState(false);
  const [start, setStart] = useState(0);
  if (!groups.length) return null;
  return (
    <section aria-label="Stories" data-testid="stories-rail" className="py-2">
      <div className="mb-3 flex items-end justify-between">
        <div>
          <p className="lux-label">{title}</p>
          {subtitle && <p className="mt-1 text-xs text-lux-muted">{subtitle}</p>}
        </div>
        <p className="text-xs text-lux-muted">Visible for 24 hours</p>
      </div>
      <div className="lux-scroll-x -mx-4 flex gap-4 overflow-x-auto px-4 pb-1 sm:gap-5">
        {groups.map((g, i) => (
          <button
            key={g.profile_slug}
            onClick={() => {
              setStart(i);
              setOpen(true);
            }}
            className="group flex w-[72px] shrink-0 flex-col items-center gap-2 rounded-lg"
            data-testid="stories-rail-story"
            aria-label={`Open stories from ${g.display_name}`}
          >
            <span className="story-ring h-[68px] w-[68px] group-hover:border-lux-silverBright">
              <span className="block h-full w-full overflow-hidden rounded-full bg-lux-surface">
                <img src={media(g.cover)} alt="" loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
              </span>
            </span>
            <span className="w-full truncate text-center text-xs text-lux-text/90">{g.display_name}</span>
          </button>
        ))}
      </div>
      <StoryViewer groups={groups} startIndex={start} open={open} onOpenChange={setOpen} />
    </section>
  );
}
